import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CustomPieChart from '../../../components/visualisation/Pie Chart';
import LinearProgressWithLabel from '../../../components/visualisation/LinearProgressWithPercent';
import dayjs from 'dayjs';

const SessionEntry = ({ session }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();

    const handleParentClick = () => {
        navigate("/diary-entry", { state: { session }});
    };

    const handleExpandClick = (event) => {
        event.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const getFormattedDuration = (start, end) => {
        const durationInMins = Math.floor((end - start) / 60000);
        const hours = Math.floor(durationInMins / 60);
        const mins = durationInMins % 60;
        return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
    };

    const formattedDuration = getFormattedDuration(new Date(session.session_date), new Date(session.end_time));

    const moodIcons = [
        <SentimentVeryDissatisfiedIcon sx={{ fontSize: '1.25rem' }} className="text-red-500" />,
        <SentimentDissatisfiedIcon sx={{ fontSize: '1.25rem' }} className="text-orange-500" />,
        <SentimentNeutralIcon sx={{ fontSize: '1.25rem' }} className="text-yellow-500" />,
        <SentimentSatisfiedIcon sx={{ fontSize: '1.25rem' }} className="text-green-500" />,
        <SentimentVerySatisfiedIcon sx={{ fontSize: '1.25rem' }} className="text-green-600" />
    ];

    const getStatusColor = (status) => {
        return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
    };

    const StatCard = ({ icon, label, value, className = "" }) => (
        <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
            <div className="flex items-center space-x-2 mb-1">
                {icon}
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{label}</span>
            </div>
            <p className="p-style text-lg font-semibold text-gray-900">{value}</p>
        </div>
    );

    const ProgressCard = ({ label, value, className = "" }) => (
        <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
            <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{label}: </span>
                <span className="text-sm font-semibold text-gray-900"> {value}%</span>
            </div>
            <LinearProgressWithLabel progress={value} />
        </div>
    );

    return (
        <div className="cursor-pointer hover:shadow-lg transition-all duration-200" onClick={handleParentClick}>
            {/* Header Section */}
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {dayjs(session.session_date).format("MMM DD, YYYY")}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(session.status)}`}>
                                {session.status === 'active' ? 'Active' : 'Completed'}
                            </span>
                        </div>
                        <p className="text-base font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-md inline-block">
                            {session.discipline}
                        </p>
                    </div>
                    
                    {(session.status === 'closed' || session.session_aims !== null) &&
                        <button
                            onClick={handleExpandClick}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {isExpanded ? 
                                <ExpandLessIcon className="text-gray-500" /> : 
                                <ExpandMoreIcon className="text-gray-500" />
                            }
                        </button>
                    }
                </div>

                {/* Quick Stats for Active Sessions */}
                {session.status === 'active' && (
                    <div className="flex items-center space-x-4 text-base text-gray-600">
                        <div className="flex items-center space-x-1">
                            <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                            <span>Started {dayjs(session.session_date).format("HH:mm A")}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <span>Mood:</span>
                            {moodIcons[session.start_mood]}
                        </div>
                        <div className="flex items-center space-x-1">
                            <span>Fatigue </span>
                            : {session.start_fatigue} / 10
                        </div>
                    </div>
                )}

                {/* Quick Stats for Completed Sessions */}
                {session.status === 'closed' && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {session.end_time && (
                            <div className="flex items-center space-x-2 text-base text-gray-600">
                                <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                                <span>{formattedDuration}</span>
                            </div>
                        )}
                        <div className="flex items-center space-x-2 text-base text-gray-600">
                            <FitnessCenterIcon sx={{ fontSize: '1rem' }} />
                            <span>{session.total_exercises} exercises</span>
                        </div>
                        <div className="flex items-center space-x-2 text-base text-gray-600">
                            <EmojiEventsIcon sx={{ fontSize: '1rem' }} />
                            <span>{session.total_skills} skills</span>
                        </div>
                        <div className="flex items-center space-x-1 text-base text-gray-600">
                            <span>Mood:</span>
                            {moodIcons[session.start_mood]}
                            <span>→</span>
                            {moodIcons[session.end_mood]}
                        </div>
                        {session.start_fatigue > 0 && 
                            <div className="flex items-center space-x-1 text-base text-gray-600">
                                <span>Fatigue:</span>
                                {session.start_fatigue}
                                <span> → </span>
                                {session.end_fatigue}
                            </div>
                        }
                    </div>
                )}
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="px-6 pb-6 pt-0 border-t border-gray-100">
                    <div className="mt-4 space-y-6">
                        {/* Session Aims */}
                        {session.session_aims?.length > 1 && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Session Aims</h4>
                                <p className="p-style text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                                    {session.session_aims}
                                </p>
                            </div>
                        )}

                        {/* Completed Session Details */}
                        {session.status === 'closed' && (
                            <>
                                {/* Key Stats Grid */}
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <StatCard 
                                        icon={<FitnessCenterIcon sx={{ fontSize: '1rem' }} className="text-blue-500" />}
                                        label="Total Exercises"
                                        value={session.total_exercises}
                                    />
                                    <StatCard 
                                        icon={<EmojiEventsIcon sx={{ fontSize: '1rem' }} className="text-yellow-500" />}
                                        label="Total Skills"
                                        value={session.total_skills}
                                    />
                                    <StatCard 
                                        icon={<span className="text-purple-500 font-bold text-sm">D</span>}
                                        label="Total Difficulty"
                                        value={session.total_difficulty}
                                    />
                                    {session.average_tof > 0.0 && (
                                        <StatCard 
                                            icon={<AccessTimeIcon sx={{ fontSize: '1rem' }} className="text-indigo-500" />}
                                            label="Avg ToF"
                                            value={`${session.average_tof}s`}
                                        />
                                    )}
                                    {session.average_stopwatch > 0.0 && (
                                        <StatCard 
                                            icon={<AccessTimeIcon sx={{ fontSize: '1rem' }} className="text-indigo-600" />}
                                            label="Avg Stopwatch"
                                            value={`${session.average_stopwatch}s`}
                                        />
                                    )}
                                    {session.hydration > 0 && (
                                        <StatCard 
                                            icon={<WaterDropIcon sx={{ fontSize: '1rem' }} className="text-cyan-500" />}
                                            label="Hydration"
                                            value={`${session.hydration}ml`}
                                        />
                                    )}
                                </div>

                                {/* Progress Bars */}
                                {(session.exercise_success_rate > 0.0 || session.skills_success_rate > 0.0) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {session.exercise_success_rate > 0.0 && (
                                            <ProgressCard 
                                                label="Exercise Completion"
                                                value={session.exercise_success_rate}
                                            />
                                        )}
                                        {session.skills_success_rate > 0.0 && (
                                            <ProgressCard 
                                                label="Skills Completion"
                                                value={session.skills_success_rate}
                                            />
                                        )}
                                    </div>
                                )}

                                {/* Pie Chart */}
                                {(session.total_routines > 0 || session.total_drills > 0 || session.total_skill_attempts > 0) && (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-4 text-center">Exercise Breakdown</h4>
                                        <div className="flex justify-center">
                                            <CustomPieChart data={[
                                                { name: "Routines", value: session.total_routines },
                                                { name: "Drills", value: session.total_drills },
                                                { name: "Skills", value: session.total_skill_attempts },
                                                { name: "Other", value: session.total_exercises - (session.total_routines + session.total_drills + session.total_skill_attempts) },
                                            ]} />
                                        </div>
                                    </div>
                                )}

                                {/* Feedback and Reflection */}
                                <div className="space-y-4">
                                    {session.session_feedback?.length > 1 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Feedback</h4>
                                            <p className="p-style text-sm text-gray-600 bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
                                                {session.session_feedback}
                                            </p>
                                        </div>
                                    )}
                                    {session.session_reflection?.length > 1 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Reflection</h4>
                                            <p className="p-style text-sm text-gray-600 bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                                                {session.session_reflection}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionEntry;

// import { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { Button } from '@mui/material';
// import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
// import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
// import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
// import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
// import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
// import CustomPieChart from '../../../components/visualisation/Pie Chart';
// import LinearProgressWithLabel from '../../../components/visualisation/LinearProgressWithPercent';
// import dayjs from 'dayjs';

// const SessionEntry = ({session}) => {

//     const [state, setState] = useState(false);
//     const navigate = useNavigate();

//     const handleParentClick = () => {
//         navigate("/diary-entry", { state: { session }});
//     };

//     const handleButtonClick = (event) => {
//         event.stopPropagation();
//         setState(!state);
//     };

//     const getFormattedDuration = (start, end) => {
//         const durationInMins = Math.floor((end - start) / 60000);
//         const hours = Math.floor(durationInMins / 60);
//         const mins = durationInMins % 60;
//         return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
//     };

//     const formattedDuration = getFormattedDuration(new Date(session.session_date), new Date(session.end_time));

//     const moodIcons = [
//         <SentimentVeryDissatisfiedIcon style={{height:'1.6em'}} />,
//         <SentimentDissatisfiedIcon style={{height:'1.6em'}} />,
//         <SentimentNeutralIcon style={{height:'1.6em'}} />,
//         <SentimentSatisfiedIcon style={{height:'1.6em'}} />,
//         <SentimentVerySatisfiedIcon style={{height:'1.6em'}} />
//     ]

//     return (
//         <div className='entry' style={{cursor: 'pointer', position: 'relative', border: '2px solid var(--site-light-background-color)', borderRadius: '10px', padding: '5px', margin: '5px 0'}} onClick={handleParentClick} >
//         <h3 className="h3-style" style={{ fontWeight: 'bold'}}>
//             {dayjs(session.session_date).format("DD/MM/YY") + ' - ' + session.discipline}
//             {session.status === 'active' && <span style={{
//                     display: 'inline-block',
//                     width: '10px',
//                     height: '10px',
//                     marginLeft: '10px',
//                     borderRadius: '50%',
//                     backgroundColor: '#008000',
//             }}></span>}
//         </h3>

//         {state && <>
//             {session.session_aims?.length > 1 && <>
//                 <p style={{ fontWeight: '600'}}>Aims</p>
//                 <p>{session.session_aims}</p>
//             </>}
//             {session.status === 'active' &&
//                 <p>Start time: {dayjs(session.session_date).format("HH:mm A")}</p>
//             }
//             {session.status === 'active' && 
//                 <p>Initial Mood: <span style={{ verticalAlign: 'text-top' }}> {moodIcons[session.start_mood]}</span></p>
//             }
//             {(session.status === 'active' && session.start_fatigue > 0) &&
//                 <p>Initial Fatigue: <span style={{ verticalAlign: 'text-top' }}> {session.start_fatigue}</span></p>
//             }
//             {(session.status === 'closed') && <>
            
//                 {session.end_time !== null &&
//                     <p>Session length: {formattedDuration}</p>
//                 }
//                 <p>
//                     Mood: 
//                     <span style={{ verticalAlign: 'text-top' }}> {moodIcons[session.start_mood]}</span>
//                     <span>→</span> 
//                     <span style={{ verticalAlign: 'text-top' }}> {moodIcons[session.end_mood]}</span>
//                 </p>
//                 {session.start_fatigue > 0 &&
//                     <p>Fatigue: {session.start_fatigue} → {session.end_fatigue}</p>
//                 }
//                 <p>Total efforts:  {session.total_exercises}</p>
//                 <p>Total skills:  {session.total_skills}</p>
//                 <p>Total difficulty:  {session.total_difficulty}</p>

//                 {session.exercise_success_rate > 0.0 && <>
//                     <p>Exercise completion:  {session.exercise_success_rate}%</p>
//                     <LinearProgressWithLabel progress={session.exercise_success_rate} />
//                 </>}
//                 {session.skills_success_rate > 0.0 && <>
//                     <p>Skills completion:  {session.skills_success_rate}%</p>
//                     <LinearProgressWithLabel progress={session.skills_success_rate} />
//                 </>}
//                 {session.average_tof > 0.0 &&
//                     <p>Average ToF:  {session.average_tof}s</p>
//                 }
//                 {session.average_stopwatch > 0.0 &&
//                     <p>Average Stopwatch ToF:  {session.average_stopwatch}s</p>
//                 }
//                 {session.hydration > 0 &&
//                     <p>Water consumption: {session.hydration}ml</p>
//                 }
//                 {session.session_feedback?.length > 1 && <>
//                     <p style={{ fontWeight: '600'}}>Feedback</p>
//                     <p>{session.session_feedback}</p>
//                 </>}
//                 {session.session_reflection?.length > 1 && <>
//                     <p style={{ fontWeight: '600'}}>Reflection</p>
//                     <p>{session.session_reflection}</p>
//                 </>}
//                 {(session.total_routines > 0 || session.total_drills > 0 || session.total_skill_attempts > 0) && 
//                     <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px'}}>
//                         <CustomPieChart data={[
//                             { name: "Routines", value: session.total_routines },
//                             { name: "Drills", value: session.total_drills },
//                             { name: "Skills", value: session.total_skill_attempts },
//                             { name: "Other", value: session.total_exercises - (session.total_routines + session.total_drills + session.total_skill_attempts) },
//                         ]} />
//                     </div>
//                 }
                
//             </>}
//         </>}

//         <Button 
//             style={{
//             fontSize: '1.4rem', 
//             color: '#000', 
//             backgroundColor: 'transparent',
//             }} 
//             onClick={handleButtonClick}
//         >
//             {!state ? 'Show summary' : 'Hide summary'}
//         </Button>
//     </div>
//     )
// };

// export default SessionEntry;